import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../utils/scrollUtils';

interface HeroMobileProps {
  t: any;
  isRTL: boolean;
  language: 'fr' | 'en' | 'ar';
}

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-top: 60px; /* Account for mobile header */
  touch-action: pan-y; /* Allow vertical scrolling but prevent horizontal */
`;

const SliderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  touch-action: pan-y; /* Only allow vertical panning */
  overflow: hidden;
`;

const Slide = styled(motion.div)<{ $bgImage: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.$bgImage}) center/cover no-repeat;
  background-size: cover;
  background-position: center center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1;
  }
`;

const HeroContent = styled(motion.div)<{ $isRTL: boolean }>`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 90%;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: brightness(0) invert(1); /* Makes the logo white */
  transition: all 0.3s ease;
`;

const TextLogo = styled.img`
  width: 200px;
  height: auto;
  object-fit: contain;
  filter: brightness(0) invert(1); /* Makes the text logo white */
  transition: all 0.3s ease;
`;

const Description = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7);
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const CTAButton = styled(motion.button)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2;
`;

const Dot = styled.div<{ $isActive: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$isActive ? '#dc2626' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$isActive ? '#dc2626' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

const SwipeHint = styled(motion.div)`
  position: absolute;
  bottom: 4rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.8rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SwipeIcon = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid white;
  border-radius: 4px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 2px;
    background: white;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 2px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid white;
    border-top: 3px solid transparent;
    border-bottom: 3px solid transparent;
  }
`;

const SwipeIndicator = styled(motion.div)<{ $direction: 'left' | 'right' | null }>`
  position: absolute;
  top: 50%;
  ${props => props.$direction === 'left' ? 'right: 2rem;' : 'left: 2rem;'}
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    ${props => props.$direction === 'left' 
      ? 'border-left: 12px solid white;' 
      : 'border-right: 12px solid white;'
    }
  }
`;

const HeroMobile: React.FC<HeroMobileProps> = ({ t, isRTL, language }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const touchEndTime = useRef<number>(0);
  const lastTouchX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  const slides = [
'/images/im00.jpg',
'/images/im0.jpg',
'/images/tch.jpg',
'/images/imim.png',
    '/images/im90.png',
'/images/im5.png',
'/images/im3.png'
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlay || isTransitioning) return;

    intervalRef.current = setInterval(() => {
      setDirection('left');
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlay, isTransitioning, slides.length]);

  // Handle transition state
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  // Hide swipe hint after first interaction
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSwipeHint(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const goToSlide = (index: number) => {
    if (index === currentSlide || isTransitioning) return;
    
    setDirection(index > currentSlide ? 'left' : 'right');
    setCurrentSlide(index);
    setIsAutoPlay(false);
    setShowSwipeHint(false);
    
    // Resume auto-play after 8 seconds
    setTimeout(() => {
      setIsAutoPlay(true);
    }, 8000);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartTime.current = Date.now();
    lastTouchX.current = e.targetTouches[0].clientX;
    isDragging.current = true;
    setShowSwipeHint(false);
    setIsSwipeActive(true);
    
    // Pause auto-play during touch
    setIsAutoPlay(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    e.preventDefault();
    const currentX = e.targetTouches[0].clientX;
    const deltaX = currentX - lastTouchX.current;
    
    // Determine swipe direction based on movement
    if (Math.abs(deltaX) > 10) {
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
    }
    
    lastTouchX.current = currentX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    e.preventDefault();
    touchEndX.current = e.changedTouches[0].clientX;
    touchEndTime.current = Date.now();
    
    const distance = touchStartX.current - touchEndX.current;
    const duration = touchEndTime.current - touchStartTime.current;
    const velocity = Math.abs(distance) / duration;
    
    // More sensitive swipe detection with velocity consideration
    const minSwipeDistance = 30;
    const minVelocity = 0.1;
    
    const isLeftSwipe = distance > minSwipeDistance || (distance > 20 && velocity > minVelocity);
    const isRightSwipe = distance < -minSwipeDistance || (distance < -20 && velocity > minVelocity);

    if (isLeftSwipe) {
      setDirection('left');
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    } else if (isRightSwipe) {
      setDirection('right');
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
    
    // Reset states
    isDragging.current = false;
    setIsSwipeActive(false);
    setSwipeDirection(null);
    
    // Resume auto-play after 8 seconds
    setTimeout(() => {
      setIsAutoPlay(true);
    }, 8000);
  };

  const handleCTAClick = () => {
    scrollToSection('services-mobile');
  };

  const slideVariants = {
    enter: (direction: 'left' | 'right' | null) => ({
      x: direction === 'left' ? '100%' : direction === 'right' ? '-100%' : 0,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: 'left' | 'right' | null) => ({
      zIndex: 0,
      x: direction === 'left' ? '-100%' : direction === 'right' ? '100%' : 0,
      opacity: 0,
    }),
  };

  return (
    <HeroSection id="home">
      <SliderContainer
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <Slide
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            $bgImage={slides[currentSlide]}
          />
        </AnimatePresence>
        
        {/* Swipe indicators */}
        {isSwipeActive && swipeDirection && (
          <SwipeIndicator
            $direction={swipeDirection}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </SliderContainer>

      <HeroContent $isRTL={isRTL}>
        <LogoContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Logo src="/logo192.png" alt="TECHYID Logo" />
          <TextLogo src={language === 'ar' ? "/images/textar.png" : "/images/text.png"} alt="TECHYID" />
        </LogoContainer>
        
        
        <CTAButton
          $isRTL={isRTL}
          onClick={handleCTAClick}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          whileTap={{ scale: 0.95 }}
        >
          {t.ctaButton}
        </CTAButton>
      </HeroContent>

      <DotsContainer>
        {slides.map((_, index) => (
          <Dot
            key={index}
            $isActive={currentSlide === index}
            onClick={() => goToSlide(index)}
          />
        ))}
      </DotsContainer>

      {showSwipeHint && (
        <SwipeHint
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <SwipeIcon />
          Glisser pour naviguer
        </SwipeHint>
      )}
    </HeroSection>
  );
};

export default HeroMobile;